import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Host,
  Inject,
  input,
  InputSignal,
  Optional,
  output,
  OutputEmitterRef,
  Self,
} from '@angular/core';
import { WLCM_FORM_CONTROL, WLCM_FORM_CONTROL_PROVIDER, WlcmFormControl, WlcmFormsModule } from '@wlcm/angular/forms';
import { InputWithUnitComponent } from '@shared/components/forms/input-with-unit/input-with-unit.component';
import { ControlContainer, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { WlcmIconDirective, WlcmIconName, WlcmOption } from '@wlcm/angular/core';
import { FamilyMemberForm } from '../../models/family-member-form.models';
import { WlcmPhoneInputModule } from '@wlcm/angular/phone-input';
import { WlcmDatepickerModule } from '@wlcm/angular/datepicker';
import { HeightOptions, SUBMISSION_BREAKS, TShirtSizeOptions, WeightOptions } from '@core/constants';
import { KidFormModel, KidFormPayload, KidFormValue } from '../../models/kid-form.models';
import { Child } from '@core/models/entities/child.models';
import { MatSelectChange } from '@angular/material/select';
import { patchSelectableKidForm, transformToChildPayload } from '../../utils';
import { TrackReactiveControlDirective } from '@shared/modules/form-tracker';
import { TIME_OPTIONS } from '@core/constants/date-options.constants';
import { ClockIconComponent } from '@shared/components/icons/clock-icon/clock-icon.component';

export type KidFormPurpose = 'sign-up' | 'profile' | 'apply' | 'checklist';

@Component({
  selector: 'napa-kid-form',
  standalone: true,
  imports: [
    WlcmFormsModule,
    WlcmPhoneInputModule,
    ReactiveFormsModule,
    InputWithUnitComponent,
    WlcmDatepickerModule,
    WlcmIconDirective,
    TrackReactiveControlDirective,
    ClockIconComponent,
  ],
  templateUrl: './kid-form.component.html',
  styleUrl: './kid-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WLCM_FORM_CONTROL_PROVIDER],
})
export class KidFormComponent extends FamilyMemberForm<KidFormModel, KidFormPayload> {
  readonly WlcmIconName = WlcmIconName;

  readonly heightOptions: WlcmOption[] = HeightOptions;

  readonly weightOptions: WlcmOption[] = WeightOptions;

  readonly TShirtSizeOptions: WlcmOption[] = TShirtSizeOptions;

  readonly timeOptions: WlcmOption[] = TIME_OPTIONS;

  readonly breakOptions: WlcmOption[] = SUBMISSION_BREAKS;

  kidOptions: InputSignal<WlcmOption[]> = input<WlcmOption[]>([]);

  purpose: InputSignal<KidFormPurpose> = input.required<KidFormPurpose>();

  selected: OutputEmitterRef<Child> = output();

  maxDate: Date = new Date();

  constructor(
    protected override changeDetector: ChangeDetectorRef,
    @Inject(WLCM_FORM_CONTROL) protected override wlcmFormControl: WlcmFormControl,
    @Optional() @Host() @Self() protected override controlContainer: ControlContainer
  ) {
    super(changeDetector, wlcmFormControl, controlContainer);
  }

  get payload(): KidFormPayload {
    const formValue = this.form.getRawValue();
    return transformToChildPayload(formValue);
  }

  override get storePayload(): KidFormValue {
    return this.form.getRawValue();
  }

  get isValid(): boolean {
    return this.form.valid;
  }

  handleSelectionChange(event: MatSelectChange): void {
    const child: Child = event.value.data;

    patchSelectableKidForm(this.form, child, false);
  }

  markAllAsTouched(): void {
    this.form.markAllAsTouched();

    this.changeDetector.markForCheck();
  }

  validateControlContainer(controlContainer: ControlContainer): void {
    if (!controlContainer || !(controlContainer?.control instanceof FormGroup)) {
      throw new Error('FormGroup<KidFormModel> is not provided.');
    }
  }
}
